import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { generateToken, saveToStorage } from 'utils/token';
import { store } from 'redux/store';
import { setUser as setUserAction } from 'modules/profile/redux/userActions';
import {
  customAuthApi,
  LoginCredentials,
  LoginResponse,
  SendOtpCredentials,
  SendOtpResponse,
} from '../api/customAuth.api';

export interface CustomAuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  sub: string;
}

export interface CustomAuthContextType {
  user: CustomAuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  session: string | null;
  accessToken: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  sendOtp: (credentials: SendOtpCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshAuth: () => Promise<void>;
  clearSession: () => void;
  getAccessToken: () => string | null;
  rateLimitInfo: {
    remainingAttempts: number;
    isBlocked: boolean;
    blockedUntil?: number;
  } | null;
  clearRateLimitInfo: () => void;
}

const CustomAuthContext = createContext<CustomAuthContextType | undefined>(
  undefined
);

interface CustomAuthProviderProps {
  children: ReactNode;
}

export const CustomAuthProvider: React.FC<CustomAuthProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<CustomAuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const [rateLimitInfo, setRateLimitInfo] = useState<{
    remainingAttempts: number;
    isBlocked: boolean;
    blockedUntil?: number;
  } | null>(null);

  const storeAuthInSessionStorage = (response: LoginResponse) => {
    const authKey = `custom.user:${process.env.REACT_APP_API_URL}:${response.user.sub}`;

    const authData = {
      id_token: response.idToken,
      access_token: response.accessToken,
      refresh_token: response.refreshToken,
      token_type: 'Bearer',
      scope: 'email openid phone profile',
      profile: {
        sub: response.user.sub,
        email: response.user.email,
        name: response.user.name,
        picture: response.user.picture,
        email_verified: true,
        iss: process.env.REACT_APP_API_URL,
        aud: process.env.REACT_APP_CLIENT_ID || 'custom-client',
        token_use: 'id',
        exp: Math.floor(Date.now() / 1000) + (response.expiresIn || 3600),
        iat: Math.floor(Date.now() / 1000),
      },
      expires_at: Math.floor(Date.now() / 1000) + (response.expiresIn || 3600),
    };

    sessionStorage.setItem(authKey, JSON.stringify(authData));
    console.log('🔐 Stored auth data in sessionStorage with key:', authKey);
  };

  const getAuthFromSessionStorage = (): any => {
    const authKeys = Object.keys(sessionStorage).filter((key) =>
      key.startsWith('custom.user:')
    );

    if (authKeys.length > 0) {
      const authKey = authKeys[0];
      const authData = sessionStorage.getItem(authKey);
      if (authData) {
        try {
          return JSON.parse(authData);
        } catch (error) {
          console.error('Error parsing auth data from sessionStorage:', error);
        }
      }
    }
    return null;
  };

  const clearAuthFromSessionStorage = () => {
    const authKeys = Object.keys(sessionStorage).filter((key) =>
      key.startsWith('custom.user:')
    );

    authKeys.forEach((key) => {
      sessionStorage.removeItem(key);
      console.log('🔐 Removed auth data from sessionStorage:', key);
    });
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const sessionAuthData = getAuthFromSessionStorage();

        if (sessionAuthData && sessionAuthData.access_token) {
          const now = Math.floor(Date.now() / 1000);
          if (sessionAuthData.expires_at > now) {
            setAccessToken(sessionAuthData.access_token);
            setUser(sessionAuthData.profile);
            setIsAuthenticated(true);
            console.log('🔐 Restored auth from sessionStorage');
            return;
          }

          try {
            const refreshResponse = await customAuthApi.refreshToken(
              sessionAuthData.refresh_token
            );
            setAccessToken(refreshResponse.accessToken);
            setUser(sessionAuthData.profile);
            setIsAuthenticated(true);
            console.log('🔐 Refreshed token from sessionStorage');
            return;
          } catch (refreshError) {
            console.log(
              '🔐 Failed to refresh token from sessionStorage, clearing data'
            );
            clearAuthFromSessionStorage();
          }
        }

        const refreshToken = localStorage.getItem('refresh-token');
        const userData = localStorage.getItem('user-data');

        if (refreshToken && userData) {
          try {
            const refreshResponse = await customAuthApi.refreshToken(
              refreshToken
            );
            const parsedUser = JSON.parse(userData);

            setAccessToken(refreshResponse.accessToken);
            setUser(parsedUser);
            setIsAuthenticated(true);
          } catch (refreshError) {
            localStorage.removeItem('refresh-token');
            localStorage.removeItem('user-data');
            setAccessToken(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const sendOtp = async (credentials: SendOtpCredentials) => {
    try {
      setIsLoading(true);
      setError(null);

      const response: SendOtpResponse = await customAuthApi.sendOtp(
        credentials
      );

      if (response.session) {
        setSession(response.session);
        localStorage.setItem('otp-session', response.session);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send OTP');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessToken = () => accessToken;

  const clearRateLimitInfo = () => {
    setRateLimitInfo(null);
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      setError(null);
      clearRateLimitInfo();

      const response: LoginResponse = await customAuthApi.login(credentials);

      if (response.success && response.accessToken) {
        clearRateLimitInfo();

        localStorage.setItem('refresh-token', response.refreshToken);
        localStorage.setItem('user-data', JSON.stringify(response.user));

        setAccessToken(response.accessToken);

        storeAuthInSessionStorage(response);

        const jwtToken = generateToken(response.user);
        saveToStorage('token', jwtToken);

        setSession(null);
        localStorage.removeItem('otp-session');

        setUser(response.user);
        setIsAuthenticated(true);
      } else {
        if (response.rateLimitInfo) {
          setRateLimitInfo(response.rateLimitInfo);
        }
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      if (error.rateLimitInfo) {
        setRateLimitInfo(error.rateLimitInfo);
      }
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const clearSession = () => {
    setSession(null);
    localStorage.removeItem('otp-session');
  };

  const logout = async () => {
    try {
      const accessToken = getAccessToken();

      if (accessToken) {
        await customAuthApi.logout(accessToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('refresh-token');
      localStorage.removeItem('user-data');
      localStorage.removeItem('token');
      localStorage.removeItem('otp-session');
      clearAuthFromSessionStorage();

      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setSession(null);

      store.dispatch(setUserAction(null));
      window.location.href = window.location.origin;
    }
  };

  const refreshAuth = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh-token');
      const userData = localStorage.getItem('user-data');

      if (refreshToken && userData) {
        const response = await customAuthApi.refreshToken(refreshToken);
        const parsedUser = JSON.parse(userData);

        setAccessToken(response.accessToken);

        setUser(parsedUser);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      await logout();
    }
  };

  const value: CustomAuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    session,
    accessToken,
    login,
    sendOtp,
    logout,
    clearError,
    refreshAuth,
    clearSession,
    getAccessToken,
    rateLimitInfo,
    clearRateLimitInfo: () => {},
  };

  return (
    <CustomAuthContext.Provider value={value}>
      {children}
    </CustomAuthContext.Provider>
  );
};

export const useCustomAuth = (): CustomAuthContextType => {
  const context = useContext(CustomAuthContext);
  if (context === undefined) {
    throw new Error('useCustomAuth must be used within a CustomAuthProvider');
  }
  return context;
};
